"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const testimonials = [{
  id: 1,
  name: "Marie D.",
  role: "IELTS Student",
  image: "https://unitylanguageacademy.10web.cloud/wp-content/uploads/2026/02/Screenshot-2026-01-15-194054.jpg?v=lebo8sygl9c",
  quote: "Thanks to Unity Language Academy, I achieved my IELTS goal and secured my university place. The instructors were incredibly supportive and the exam-focused curriculum made all the difference."
}, {
  id: 2,
  name: "Jean P.",
  role: "German Language Student",
  image: "https://unitylanguageacademy.10web.cloud/wp-content/uploads/2026/02/tutor.jpg?v=x1h2xugn4u9",
  quote: "Excellent instructors and flexible schedules helped me improve my German quickly. The learning environment is professional yet welcoming, and I felt supported every step of the way."
}, {
  id: 3,
  name: "Sophie L.",
  role: "TOEFL Student",
  image: "https://unitylanguageacademy.10web.cloud/wp-content/uploads/2026/02/sophie.jpg?v=mv2o9svup1a",
  quote: "The TOEFL preparation course exceeded my expectations. The teaching methodology is practical and results-oriented, and I achieved the score I needed for my graduate program."
}, {
  id: 4,
  name: "Antoine R.",
  role: "General English Student",
  image: "https://unitylanguageacademy.10web.cloud/wp-content/uploads/2026/02/happy-face-hd-8k-wallpaper-stock-photographic_1064748-29023.jpg?v=nv2g7ry0n6l",
  quote: "Unity Language Academy transformed my English skills. The interactive learning resources and dedicated instructors helped me gain confidence in both speaking and writing."
}, {
  id: 5,
  name: "Claire M.",
  role: "German & English Student",
  image: "https://unitylanguageacademy.10web.cloud/wp-content/uploads/2026/02/girl.jpg",
  quote: "Learning both German and English here has opened so many doors for my career. The quality of instruction and the supportive community make this school truly special."
}];
export default function Testimonials() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  const slideDuration = 5000;
  useEffect(() => {
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          return 0;
        }
        return prev + 100 / (slideDuration / 50);
      });
    }, 50);
    return () => clearInterval(progressInterval);
  }, []);
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % testimonials.length);
      setProgress(0);
    }, slideDuration);
    return () => clearInterval(timer);
  }, []);
  const handlePrevious = () => {
    setCurrentIndex(prev => (prev - 1 + testimonials.length) % testimonials.length);
    setProgress(0);
  };
  const handleNext = () => {
    setCurrentIndex(prev => (prev + 1) % testimonials.length);
    setProgress(0);
  };
  return <section id="testimonials" ref={ref} className={`dark bg-background text-foreground py-16 md:py-24 px-4 overflow-hidden ${isVisible ? "animate-reveal" : "opacity-0"}`}>
      <div className="container max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="font-display text-[36px] md:text-[48px] leading-[1.2] font-bold tracking-[-0.5px] mb-4">
            Student Success Stories
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Hear from our students about their transformative learning experiences at Unity Language Academy
          </p>
        </div>

        <div className="relative min-h-[400px] md:min-h-[320px] flex items-center">
          <div className="w-full">
            {testimonials.map((testimonial, i) => <div key={testimonial.id} data-index={i} className={`absolute inset-0 transition-all duration-500 ease-smooth ${i === currentIndex ? "opacity-100 translate-x-0" : i < currentIndex ? "opacity-0 -translate-x-full" : "opacity-0 translate-x-full"}`}>
                <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12 px-4">
                  <div className="flex-shrink-0">
                    <SafeHydrate><Avatar className="w-32 h-32 md:w-40 md:h-40 border-4 border-primary/20 shadow-medium">
                      <AvatarImage src={testimonial.image} alt={testimonial.name} />
                      <AvatarFallback className="bg-primary/10 text-primary text-3xl font-semibold">
                        {testimonial.name.split(' ').map(n => n[0]).join('')}
                      </AvatarFallback>
                    </Avatar></SafeHydrate>
                  </div>

                  <div className="flex-1 text-center md:text-left">
                    <blockquote className="text-2xl md:text-3xl lg:text-4xl font-display font-semibold leading-relaxed mb-6 text-foreground">
                      "{testimonial.quote}"
                    </blockquote>
                    <div>
                      <p className="text-xl font-semibold text-primary mb-1">
                        {testimonial.name}
                      </p>
                      <p className="text-muted-foreground">
                        {testimonial.role}
                      </p>
                    </div>
                  </div>
                </div>
              </div>)}
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center gap-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="icon" onClick={handlePrevious} className="w-12 h-12 rounded-full border-2 border-primary/30 hover:border-primary hover:bg-primary/10 hover:scale-110 transition-all duration-300 ease-spring focus-ring" aria-label="Previous testimonial">
              <ChevronLeft className="w-6 h-6 text-primary" />
            </Button>

            <div className="flex items-center gap-2">
              {testimonials.map((_, i) => <Button key={i} data-index={i} variant="ghost" size="icon" onClick={() => {
              setCurrentIndex(i);
              setProgress(0);
            }} className={`w-2 h-2 p-0 rounded-full transition-all duration-300 hover:bg-primary/50 ${i === currentIndex ? "bg-primary w-8" : "bg-primary/30"}`} aria-label={`Go to testimonial ${i + 1}`} />)}
            </div>

            <Button variant="outline" size="icon" onClick={handleNext} className="w-12 h-12 rounded-full border-2 border-primary/30 hover:border-primary hover:bg-primary/10 hover:scale-110 transition-all duration-300 ease-spring focus-ring" aria-label="Next testimonial">
              <ChevronRight className="w-6 h-6 text-primary" />
            </Button>
          </div>

          <div className="w-full max-w-md">
            <Progress value={progress} className="h-1 bg-primary/20" />
          </div>
        </div>
      </div>
    </section>;
}